import React, { useEffect, useState } from 'react';
import { Edit, Trash2, ExternalLink, Activity, Search, AlertTriangle, X, Save, Upload } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const CampaignList = () => {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal States
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; id: number | null }>({ isOpen: false, id: null });
  const [editModal, setEditModal] = useState<{ isOpen: boolean; data: any }>({ isOpen: false, data: null });
  
  // Edit Image States
  const [editImages, setEditImages] = useState<File[]>([]);
  const [editPreviews, setEditPreviews] = useState<string[]>([]);

  // 1. Fetch Data from Backend
  const fetchCampaigns = async () => {
    try {
      // const response = await fetch("http://localhost:1415/api/campaigns/all");
      // const response = await fetch("http://localhost/seva-backend/api/campaigns/list.php");
      const response = await fetch("https://www.sevaafoundation.com/seva-backend/api/campaigns/list.php");
      const data = await response.json();
      if (data.success) {
        setCampaigns(data.campaigns);
        setFilteredCampaigns(data.campaigns); // Default pe saare dikhenge
      }
    } catch (error) {
      toast.error("Failed to load campaigns");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  // Search Logic
  useEffect(() => {
    const results = campaigns.filter(camp => 
      camp.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      camp.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCampaigns(results);
  }, [searchTerm, campaigns]);

  // 2. DELETE LOGIC
  const confirmDelete = async () => {
    if (!deleteModal.id) return;
    const token = localStorage.getItem("sevaa_admin_token");
    
    try {
      // const response = await fetch(`http://localhost:1415/api/campaigns/delete/${deleteModal.id}`, {
      // const response = await fetch(`http://localhost/seva-backend/api/campaigns/delete.php?=${deleteModal.id}`, {
      const response = await fetch(`https://www.sevaafoundation.com/seva-backend/api/campaigns/delete.php?=${deleteModal.id}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success("Campaign Deleted Successfully!");
        setCampaigns(campaigns.filter(c => c.id !== deleteModal.id)); 
        setDeleteModal({ isOpen: false, id: null }); 
      } else {
        toast.error("Failed to delete");
      }
    } catch (error) {
      toast.error("Server Error");
    }
  };

  // 3. EDIT LOGIC
  const openEditModal = (camp: any) => {
    // Format Date for Input type="date"
    const formatSafeDate = (dateString: string) => dateString ? new Date(dateString).toISOString().split('T')[0] : '';
    
    setEditModal({ 
      isOpen: true, 
      data: {
        ...camp,
        start_date: formatSafeDate(camp.start_date),
        end_date: formatSafeDate(camp.end_date)
      } 
    });

    // Set Existing Images Preview
    if (camp.image_url) {
      // const existingImages = camp.image_url.split(',').map((url: string) => `http://localhost:1415${url}`);
      // const existingImages = camp.image_url.split(',').map((url: string) => `http://localhost/seva-backend${url}`);
      const existingImages = camp.image_url.split(',').map((url: string) => `https://www.sevaafoundation.com/seva-backend${url}`);
      setEditPreviews(existingImages);
    } else {
      setEditPreviews([]);
    }
    setEditImages([]);
  };

  const handleEditImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      setEditImages(filesArray);
      setEditPreviews(filesArray.map(file => URL.createObjectURL(file)));
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = localStorage.getItem("sevaa_admin_token");
    const loadingToast = toast.loading("Updating Campaign...");

    const formDataToSend = new FormData();
    formDataToSend.append('title', editModal.data.title);
    formDataToSend.append('category', editModal.data.category);
    formDataToSend.append('goal_amount', editModal.data.goal_amount);
    formDataToSend.append('short_description', editModal.data.short_description);
    formDataToSend.append('full_description', editModal.data.full_description || '');
    formDataToSend.append('start_date', editModal.data.start_date);
    formDataToSend.append('end_date', editModal.data.end_date);
    
    // Append new images if selected
    editImages.forEach(img => formDataToSend.append('images', img));

    try {
      const response = await fetch(`https://www.sevaafoundation.com/seva-backend/api/campaigns/update.php?=${editModal.data.id}`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${token}` },
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success("Campaign Updated!", { id: loadingToast });
        fetchCampaigns(); // Refresh Data from backend
        setEditModal({ isOpen: false, data: null }); 
        setEditImages([]);
      } else {
        toast.error(data.error || "Update failed", { id: loadingToast });
      }
    } catch (error) {
      toast.error("Server Error", { id: loadingToast });
    }
  };

  const getFirstImage = (imageString: string) => {
    if (!imageString) return 'https://via.placeholder.com/400x200?text=No+Image';
    // return `http://localhost:1415${imageString.split(',')[0]}`;
    // return `http://localhost/seva-backend${imageString.split(',')[0]}`;
    return `https://www.sevaafoundation.com/seva-backend${imageString.split(',')[0]}`;
  };

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <Toaster position="top-right" />
      
      {/* Header & Search Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 tracking-tight flex items-center gap-3">
            <Activity className="text-orange-500" size={32} /> Live Campaigns
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage, edit, or remove your active fundraising campaigns.</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            placeholder="Search campaigns..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500 shadow-sm" 
          />
        </div>
      </div>

      {/* Grid of Campaigns */}
      {isLoading ? (
        <div className="text-center py-20 text-slate-500 font-medium">Loading campaigns from database...</div>
      ) : filteredCampaigns.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-300">
          <p className="text-slate-500 font-medium">No campaigns found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredCampaigns.map((camp) => (
            <div key={camp.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group flex flex-col">
              
              {/* Image Preview */}
              <div className="relative h-48 overflow-hidden rounded-t-2xl">
                <img src={getFirstImage(camp.image_url)} alt={camp.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-3 right-3 bg-green-500 text-white text-[10px] uppercase px-2.5 py-1 rounded-full font-black tracking-wider shadow-sm flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div> LIVE
                </div>
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-slate-700 text-[10px] uppercase px-2 py-1 rounded font-bold">
                  {camp.category}
                </div>
              </div>

              {/* Campaign Info */}
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-bold text-lg text-slate-800 mb-1 line-clamp-1" title={camp.title}>{camp.title}</h3>
                <p className="text-sm text-slate-500 line-clamp-2 mb-4 flex-1">{camp.short_description}</p>
                
                {/* Progress Data */}
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1.5">
                    {/* Goal Amount formatting */}
                    <span className="font-semibold text-slate-700">Goal: ₹{Number(camp.goal_amount).toLocaleString('en-IN')}</span>
                    {/* Raised Amount formatting (Real data from backend) */}
                    <span className="text-orange-600 font-bold">₹{Number(camp.raised_amount || 0).toLocaleString('en-IN')} Raised</span>
                  </div>
                  <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                    {/* Percentage Progress Bar */}
                    <div 
                      className="bg-orange-500 h-full rounded-full transition-all duration-1000" 
                      style={{ width: `${Math.min(((camp.raised_amount || 0) / camp.goal_amount) * 100, 100) || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t border-slate-100">
                  <button 
                    onClick={() => openEditModal(camp)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-slate-50 text-slate-600 py-2 rounded-xl font-semibold hover:bg-slate-900 hover:text-white transition-colors text-sm"
                  >
                    <Edit size={16} /> Edit
                  </button>
                  <button 
                    onClick={() => setDeleteModal({ isOpen: true, id: camp.id })}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 text-red-600 py-2 rounded-xl font-semibold hover:bg-red-600 hover:text-white transition-colors text-sm"
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                  <button className="p-2 bg-slate-50 text-slate-400 rounded-xl hover:text-orange-500 hover:bg-orange-50 transition-colors" title="View Public Page">
                    <ExternalLink size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===================== DELETE CONFIRMATION MODAL ===================== */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full mx-4 shadow-2xl animate-fade-in-up">
            <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={32} />
            </div>
            <h2 className="text-2xl font-bold text-center text-slate-800 mb-2">Are you sure?</h2>
            <p className="text-center text-slate-500 mb-6 text-sm">Do you really want to delete this campaign? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteModal({ isOpen: false, id: null })} className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-colors">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl shadow-lg transition-colors">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== EDIT CAMPAIGN MODAL ===================== */}
      {editModal.isOpen && editModal.data && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm py-6">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full mx-4 shadow-2xl max-h-full overflow-y-auto custom-scrollbar animate-fade-in-up">
            
            <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-2 border-b border-slate-100">
              <h2 className="text-2xl font-bold text-slate-800">Edit Campaign</h2>
              <button onClick={() => setEditModal({ isOpen: false, data: null })} className="p-2 bg-slate-100 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="space-y-4">
              
              {/* Image Upload Area */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Campaign Images</label>
                <div className="relative border-2 border-dashed border-slate-200 hover:border-orange-400 bg-slate-50 rounded-xl p-4 flex flex-col items-center justify-center min-h-[120px] cursor-pointer">
                  <Upload className="text-slate-400 mb-2" size={24} />
                  <p className="text-xs text-slate-500 font-medium">Upload new images (overwrites existing)</p>
                  <input type="file" multiple accept="image/*" onChange={handleEditImageChange} className="absolute inset-0 opacity-0 cursor-pointer" />
                </div>
                {/* Previews */}
                {editPreviews.length > 0 && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {editPreviews.map((src, i) => (
                      <img key={i} src={src} className="w-16 h-16 object-cover rounded-lg border border-slate-200" alt="Preview" />
                    ))}
                  </div>
                )}
              </div>

              {/* Title & Category & Goal */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title</label>
                <input type="text" required value={editModal.data.title} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, title: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Category</label>
                  <select value={editModal.data.category} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, category: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500">
                    <option>Education</option><option>Environment</option><option>Medical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Goal Amount (₹)</label>
                  <input type="number" required value={editModal.data.goal_amount} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, goal_amount: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Start Date</label>
                  <input type="date" required value={editModal.data.start_date} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, start_date: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">End Date</label>
                  <input type="date" required value={editModal.data.end_date} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, end_date: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              </div>

              {/* Descriptions */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Short Description</label>
                <textarea rows={2} required value={editModal.data.short_description} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, short_description: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Full Description</label>
                <textarea rows={3} value={editModal.data.full_description} onChange={(e) => setEditModal({ ...editModal, data: { ...editModal.data, full_description: e.target.value } })} className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-orange-500" />
              </div>

              <button type="submit" className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white font-bold py-3.5 rounded-xl shadow-lg transition-all flex items-center justify-center gap-2">
                <Save size={20} /> Save Changes
              </button>
            </form>

          </div>
        </div>
      )}

    </div>
  );
};

export default CampaignList;